'use client';

import { useTranslations } from 'next-intl';
import { useNextStepLabel } from 'components/ring-air/hooks/useNextStepLabel';
import { analytics } from 'lib/analytics/analytics';
import { ShippingBox } from 'assets/icons';
import {
  MidPanel,
  MidPanelPhone,
  LeftPanel,
  RightPanel,
} from 'components/shared/BottomBar';
import { useContext } from 'react';
import {
  getEngravingKey,
  productMap_air_ring,
  RingSizes,
} from 'content/products/ring';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { CustomImage as Image, DotSpinner, useModal } from '..';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { RingSelectionContext } from './RingSelectionContext';
import { sddCheckoutEta } from './DeliveryEtaBadge/useSddNddEligibility';
import { aeDeliveryEtaShortCopy } from './AeDeliveryEta/copy';
import { useAeDeliveryEta } from './AeDeliveryEta/useAeDeliveryEta';
import { AE_DELIVERY_ETA_ENABLED } from 'utils/featureFlags';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { ApplePayExpressButton } from 'components/shared/ApplePayExpressButton';
import { hasMaxSelected } from 'content/products/max';
import { useRetouchFlowContext } from 'components/shared/RetouchFlow';
import styles from './BottomBar.module.css';
import {
  RING_BUY_GTM_EVENTS,
  trackRingBuyOption,
} from './analytics/ringBuyEvents';

const PAYMENT_ICON_BASE = 'web_v2/icons/payment-method-circle';
const DEFAULT_PAYMENT_ICONS = [
  `${PAYMENT_ICON_BASE}/visa.png`,
  `${PAYMENT_ICON_BASE}/mastercard.png`,
  `${PAYMENT_ICON_BASE}/apple-pay.png`,
];
const PAYMENT_ICONS_BY_STORE: Record<string, string[]> = {
  IN: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/gpay.png`,
  ],
  MX: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/paypal.png`,
  ],
  ROW: [
    `${PAYMENT_ICON_BASE}/visa.png`,
    `${PAYMENT_ICON_BASE}/mastercard.png`,
    `${PAYMENT_ICON_BASE}/paypal.png`,
  ],
};

export const BottomBar = ({ className }: { className?: string }) => {
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const RingSelectionClient = useContext(RingSelectionContext);
  const nextStepLabel = useNextStepLabel(RingSelectionClient.activeStepId);
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, taxString: s.taxString })),
  );
  const ModalClient = useModal();
  const { resetCounter: resetRetouchCounter } = useRetouchFlowContext();
  const formatPriceWithComma = useFormatPrice();
  const { region } = useRegionLocale();
  // AE window-rule ETA gate: deliberately the URL region, NOT the Shopify
  // store — QA/BH/KW/OM share ShopifyStore.AE and must not see the promise.
  const isAeDeliveryVisible =
    region?.toUpperCase() === 'AE' && AE_DELIVERY_ETA_ENABLED;
  const aeEta = useAeDeliveryEta(isAeDeliveryVisible);

  const {
    size,
    canAddToCart,
    shippingEstimate,
    sddNdd,
    color,
    defaultCurrency: currency,
    engraving,
    notServicable,
    goToNextStep,
    isLoading,
    tradeInDevice,
    tradeInCredit,
    maxCart,
  } = RingSelectionClient;

  const handleCheckout = () => {
    if (!RingSelectionClient.size) {
      analytics.track(
        'select_content',
        {
          content_type: 'cta',
          content_id: 'select_color_size',
          product: 'ring_air',
        },
        'Bottom bar select color-size - CLICK',
      );
    }
    openCheckoutInfo();
  };

  const openCheckoutInfo = () => {
    handleProceedClick();
    return;
  };

  const handleProceedClick = () => {
    if (isLoading) return;
    resetRetouchCounter(); // Reset retouch flow counter on Add to Cart
    RingSelectionClient.checkout('bottom_bar');
    ModalClient.hide();
  };

  const sctollToOrderSummary = () => {
    trackRingBuyOption(RING_BUY_GTM_EVENTS.BOTTOM_BAR_TOTAL, {
      effectivePrice: RingSelectionClient.effectivePrice,
    });
    const ref = document.getElementById('order-summary-card');
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const { store } = CartClient;

  const paymentIcons = store
    ? (PAYMENT_ICONS_BY_STORE[store] ?? DEFAULT_PAYMENT_ICONS)
    : DEFAULT_PAYMENT_ICONS;

  const PaymentMethodsIcon = (
    <div className={styles.offerIcons}>
      {paymentIcons.map((src, i) => (
        <Image
          key={i}
          src={src}
          alt=""
          height="24"
          width="24"
          className={styles.offerIcon}
        />
      ))}
    </div>
  );

  const MidPriceSection = (
    <>
      <MidPanel className={styles.midPanel}>
        {PaymentMethodsIcon}
        <div>
          <p className={`${styles.totalPriceSingle} total-price-single`}>
            {RingSelectionClient.currencySymbol}
            {formatPriceWithComma(RingSelectionClient.effectivePrice)}
            <span className={styles.taxDisclaimer}>
              ({CartClient.taxString})
            </span>
          </p>
          {tradeInDevice && tradeInCredit > 0 && (
            <p className={styles.tradeInCredit}>
              -{RingSelectionClient.currencySymbol}
              {formatPriceWithComma(tradeInCredit)}{' '}
              {tCommon('bottomBar.text.tradeCredits')}
            </p>
          )}
        </div>
      </MidPanel>

      <MidPanelPhone
        className={styles.midPanelPhone}
        onClick={sctollToOrderSummary}
      >
        <div className={styles.midPanelPhoneRow}>
          <div className={styles.subtitleTextBtn}>
            {tCommon('bottomBar.content.total')}
          </div>
          <div className={styles.midPanelPhoneRight}>
            {PaymentMethodsIcon}
            <div className={styles.midPanelPhonePrice}>
              <p className={`${styles.totalPriceSingle} total-price-single`}>
                {RingSelectionClient.currencySymbol}
                {formatPriceWithComma(RingSelectionClient.effectivePrice)}
                <span className={styles.taxDisclaimer}>
                  ({CartClient.taxString})
                </span>
              </p>
              {tradeInDevice && tradeInCredit > 0 && (
                <p className={styles.tradeInCredit}>
                  -{RingSelectionClient.currencySymbol}
                  {formatPriceWithComma(tradeInCredit)}{' '}
                  {tCommon('bottomBar.text.tradeCredits')}
                </p>
              )}
            </div>
          </div>
        </div>
      </MidPanelPhone>
    </>
  );

  if (notServicable === true) return null;

  const checkoutEta = sddCheckoutEta(sddNdd.state);
  // Window-rule ETA mirror of sddCheckoutEta above: the short SDD/NDD line
  // shows only while the sizing kit (RingSizes.OPEN) is selected — the
  // express promise is sizing-kit only, numeric sizes fall through to the
  // backend shippingEstimate line. Ineligible emirates resolve to next-day
  // inside aeDeliveryEtaShortCopy.
  const aeCheckoutEta =
    isAeDeliveryVisible && size === RingSizes.OPEN
      ? aeDeliveryEtaShortCopy(
          aeEta.selectedEmirate,
          aeEta.group,
          aeEta.windowState,
          t,
        )
      : null;

  return (
    <div
      className={
        className
          ? `${styles.root} u-grid ${className}`
          : `${styles.root} u-grid`
      }
      data-bottom-bar
    >
      <LeftPanel>
        {/* <p className="chat-specialist">
          {RingSelectionClient.price} --- {RingSelectionClient.effectivePrice}
        </p> */}
        {checkoutEta ? (
          <p className={styles.chatSpecialist}>
            <ShippingBox /> {checkoutEta}
          </p>
        ) : aeCheckoutEta ? (
          <p className={styles.chatSpecialist}>
            <ShippingBox /> {aeCheckoutEta}
          </p>
        ) : shippingEstimate ? (
          <p className={styles.chatSpecialist}>
            <ShippingBox /> {shippingEstimate}
          </p>
        ) : null}
      </LeftPanel>

      {RingSelectionClient.size ? MidPriceSection : null}

      <RightPanel>
        <div className={styles.ctaStack}>
          {hasMaxSelected(maxCart) ? null : (
            <ApplePayExpressButton
              totalAmount={RingSelectionClient.effectivePrice ?? 0}
              getLineItems={RingSelectionClient.getExpressCheckoutLineItems}
              ringColor={RingSelectionClient.color ?? undefined}
              product="Ultrahuman Ring AIR"
              onCheckoutBlocked={canAddToCart ? undefined : goToNextStep}
            />
          )}
          <PrimaryButton
            onClick={canAddToCart ? handleCheckout : goToNextStep}
            disabled={canAddToCart && isLoading}
            aria-busy={canAddToCart && isLoading}
            data-buttontype={canAddToCart ? 'add to cart' : 'go to next step'}
            data-placement="bottom_bar"
            data-variantId={
              color && size && currency
                ? productMap_air_ring[color]?.[size]?.[
                    getEngravingKey(engraving)
                  ]?.variantId[store as ShopifyStore]
                : ''
            }
            data-product="Ultrahuman Ring AIR"
          >
            {canAddToCart && isLoading ? (
              <DotSpinner color="#000000" />
            ) : canAddToCart ? (
              t('ring.productSelection.cartSummary.addToCart')
            ) : (
              nextStepLabel
            )}
          </PrimaryButton>
        </div>
      </RightPanel>
    </div>
  );
};
